<script setup lang="ts">
import { PropType } from 'vue';
import { cn } from '../lib/utils';
import { CircleAlert, CircleHelp } from 'lucide-vue-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/alert-dialog';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: false,
    default: ''
  },
  message: {
    type: String,
    required: false,
    default: ''
  },
  options: {
    type: Object as PropType<{ type: 'alert' | 'confirm' }>,
    required: true
  }
});

const emit = defineEmits<{
  (e: 'confirm', arg: boolean): void;
}>();
</script>

<template>
  <AlertDialog :open="props.show">
    <AlertDialogContent>
      <AlertDialogHeader>
        <div class="flex flex-row gap-4 items-center">
          <div class="text-gray-400 shrink-0 w-[30px]">
            <CircleAlert
              v-if="props.options.type == 'alert'"
              :size="30"
            />
            <CircleHelp
              v-else
              :size="30"
            />
          </div>

          <div>
            <AlertDialogTitle class="text-gray-300">
              {{ props.title }}
            </AlertDialogTitle>
            <AlertDialogDescription class="my-2 text-md">
              <span class="text-gray-400">
                {{ props.message }}
              </span>
            </AlertDialogDescription>
          </div>
        </div>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <div
          class="flex flex-row w-full"
          :class="{ 'justify-between': props.options.type == 'confirm',
                    'justify-end': props.options.type == 'alert' }"
        >
          <AlertDialogCancel
            v-if="props.options.type == 'confirm'"
            :class="cn('dark:text-white border border-gray-600 px-6 dark:hover:bg-gray-600 text-md')"
            @click="() => emit('confirm', false)"
          >
            <span class="text-gray-300">{{ $t('common.cancel') }}</span>
          </AlertDialogCancel>
          <AlertDialogAction
            :class="cn('dark:text-black bright:text-black text-black px-8 bg-white dark:hover:bg-gray-400 text-md')"
            @click="() => emit('confirm', true)"
          >
            <span class="dark:text-black text-black">{{ $t('common.ok') }}</span>
          </AlertDialogAction>
        </div>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
